import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, RotateCcw, ClipboardCheck, Lightbulb } from 'lucide-react';
import { api } from '../lib/api';
import SaveFinderResult from '../components/SaveFinderResult';
import usePageContent from '../hooks/usePageContent';
import { MachineCard } from './Machines';
import { buildContactHref } from '../components/CTASection';
import useSEO from '../hooks/useSEO';

const STEPS = [
    { key: 'job', title: 'What will you clean most?', options: [
        ['detailing', 'Vehicles & detailing'], ['food', 'Food & beverage equipment'], ['molds', 'Molds & plastics'],
        ['machinery', 'Production machinery'], ['electrical', 'Electrical & power equipment'], ['restoration', 'Fire, mold & restoration'],
    ] },
    { key: 'dirt', title: 'How heavy is the buildup?', options: [['light', 'Light — dust, grime, release agents'], ['medium', 'Medium — grease, oil, food residue'], ['heavy', 'Heavy — baked-on carbon, thick buildup']] },
    { key: 'often', title: 'How often will you run it?', options: [['occasional', 'Occasional jobs'], ['weekly', 'A few times a week'], ['daily', 'Daily / production cleaning']] },
    { key: 'air', title: 'What air supply do you have?', options: [['small', 'Under 100 CFM'], ['mid', '100 – 185 CFM'], ['large', '185 CFM or more'], ['unsure', 'Not sure / need a compressor']] },
];

const TIPS = {
    detailing: 'Use a fine, low-flow nozzle for paint-safe cleaning of undercarriages, engines and interiors.',
    food: 'Dry ice leaves no water or chemical residue — clean in place and get lines back up faster.',
    molds: 'Clean molds hot and in the press — no cool-down or disassembly needed.',
    machinery: 'A wider fan nozzle covers large surfaces quickly; switch to a round nozzle for tight spots.',
    electrical: 'Non-conductive and dry — always follow your lockout/tagout and safety procedures.',
    restoration: 'Removes soot and mold staining from wood and masonry without soaking the structure.',
};

const tierFor = ({ dirt, often, air }) => {
    let score = 0;
    if (dirt === 'heavy') score += 2; else if (dirt === 'medium') score += 1;
    if (often === 'daily') score += 2; else if (often === 'weekly') score += 1;
    if (air === 'large') score += 1;
    if (air === 'small') score -= 1;
    return score >= 3 ? 'industrial' : 'light';
};

const TIER_CATEGORY = { light: 'detailing-light-industrial', industrial: 'industrial-performance' };
const TIER_LABEL = { light: 'Compact / light-industrial machine', industrial: 'Industrial performance machine' };

const Options = ({ step, onPick, selected }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`finder-step-${step.key}`}>
        {step.options.map(([value, label]) => (
            <button key={value} onClick={() => onPick(value)}
                className={`text-left p-5 border transition-[border-color,background-color] ${selected === value ? 'border-brand-primary bg-brand-primary/10' : 'border-neutral-200 bg-white hover:border-neutral-900'}`}
                data-testid={`finder-option-${step.key}-${value}`}>
                <span className="font-display font-bold uppercase text-lg text-neutral-900">{label}</span>
            </button>
        ))}
    </div>
);

const Result = ({ answers, catalog, rules, onRestart }) => {
    const tier = tierFor(answers);
    const pick = rules?.[tier] || {};
    const machines = catalog.filter((m) => m.machine_type === 'machine');
    const pinned = machines.find((m) => m.id === pick.machine_id);
    const recs = pinned ? [pinned] : machines.filter((m) => m.category === TIER_CATEGORY[tier]).sort((a, b) => Number(b.featured) - Number(a.featured)).slice(0, 2);
    const nozzles = catalog.filter((m) => m.machine_type === 'accessory');
    const nozzle = nozzles.find((m) => m.id === pick.nozzle_id) || nozzles.find((m) => /nozzle|flex-case/i.test(m.name));
    const label = (k) => STEPS.find((s) => s.key === k).options.find((o) => o[0] === answers[k])?.[1];
    const summary = STEPS.map((s) => `${s.title} ${label(s.key)}`).join('\n');
    const setup = [recs.map((m) => m.name).join(' or '), nozzle?.name].filter(Boolean).join(' + ');
    const quoteHref = `${buildContactHref({ subject: `Machine Finder quote: ${setup || TIER_LABEL[tier]}`, machine: recs[0]?.name, interest: 'quote' })}&message=${encodeURIComponent(`Machine Finder answers:\n${summary}\n\nRecommended setup: ${setup || TIER_LABEL[tier]}`)}`;

    return (
        <div data-testid="finder-result">
            <div className="label-caps text-brand-primary mb-3">/ Your recommendation</div>
            <h2 className="font-display font-black uppercase text-4xl md:text-5xl text-neutral-900" data-testid="finder-result-tier">{TIER_LABEL[tier]}</h2>
            <p className="mt-4 text-neutral-600 max-w-2xl" data-testid="finder-result-note">{pick.note}</p>
            {answers.air === 'unsure' && <p className="mt-2 text-sm text-amber-800" data-testid="finder-air-note">Not sure about air? We&apos;ll size the right compressor with your quote.</p>}

            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
                {recs.map((m) => <MachineCard key={m.id} m={m} />)}
                {nozzle && (
                    <div data-testid="finder-nozzle">
                        <div className="label-caps text-xs text-neutral-500 mb-2">Suggested nozzle</div>
                        <MachineCard m={nozzle} />
                    </div>
                )}
                {recs.length === 0 && <p className="text-neutral-600" data-testid="finder-no-machine">We&apos;ll recommend the right machine with your quote.</p>}
            </div>

            <div className="mt-8 border border-neutral-200 bg-neutral-50 p-5 flex gap-3 max-w-3xl" data-testid="finder-tip">
                <Lightbulb className="h-5 w-5 text-brand-primary flex-shrink-0 mt-0.5" /><p className="text-sm text-neutral-700">{TIPS[answers.job]}</p>
            </div>

            <div className="mt-8">
                <SaveFinderResult payload={{
                    answers, tier, tier_label: TIER_LABEL[tier],
                    answer_labels: STEPS.map((s) => `${s.title} ${label(s.key)}`),
                    product_ids: [...recs.map((m) => m.id), nozzle?.id].filter(Boolean),
                    result_path: `/machine-finder?${new URLSearchParams(answers).toString()}`,
                }} />
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
                <Link to={quoteHref} onClick={() => window.trackConversion && window.trackConversion('finder_quote_click', { tier })}
                    className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white font-bold px-7 py-4 label-caps transition-colors" data-testid="finder-quote-cta">
                    <ClipboardCheck className="h-4 w-4" /> Get a quote for this setup
                </Link>
                <Link to="/rental" className="inline-flex items-center gap-2 border border-neutral-300 hover:border-neutral-900 text-neutral-900 px-7 py-4 label-caps transition-colors" data-testid="finder-rent-cta">Rent one first</Link>
                <button onClick={onRestart} className="inline-flex items-center gap-2 px-4 py-4 label-caps text-neutral-600 hover:text-neutral-900" data-testid="finder-restart"><RotateCcw className="h-4 w-4" /> Start over</button>
            </div>
        </div>
    );
};

const SurfaceSolutionFinder = () => {
    useSEO({
        title: 'Machine Finder — Which Dry Ice Blasting Machine & Nozzle Is Right for Your Job?',
        description: 'Answer 4 quick questions about your job, buildup, usage and air supply to get a dry ice blasting machine and nozzle recommendation from Dry Ice Bulls.',
    });
    const content = usePageContent();
    const [catalog, setCatalog] = useState([]);
    const [params, setParams] = useSearchParams();
    const fromUrl = () => {
        const a = Object.fromEntries(STEPS.map((s) => [s.key, params.get(s.key)]));
        return STEPS.every((s) => s.options.some((o) => o[0] === a[s.key])) ? a : null;
    };
    const [answers, setAnswers] = useState(() => fromUrl() || {});
    const [stepIdx, setStepIdx] = useState(() => (fromUrl() ? STEPS.length : 0));
    useEffect(() => {
        if (stepIdx >= STEPS.length) setParams(answers, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepIdx]);
    useEffect(() => { api.get('/machines').then((r) => setCatalog(r.data)); }, []);
    const step = STEPS[stepIdx];
    const progress = useMemo(() => Math.round((Math.min(stepIdx, STEPS.length) / STEPS.length) * 100), [stepIdx]);

    const pick = (value) => {
        setAnswers({ ...answers, [step.key]: value });
        setStepIdx(stepIdx + 1);
    };
    const restart = () => { setAnswers({}); setStepIdx(0); setParams({}, { replace: true }); };

    return (
        <div className="container-page pt-32 pb-24" data-testid="solution-finder-page">
            <div className="label-caps text-brand-primary mb-4">/ Machine Finder</div>
            <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-7xl text-neutral-900">Find your<br />machine.</h1>
            <p className="mt-6 text-neutral-600 max-w-2xl">Four quick questions. We&apos;ll match a dry ice blasting machine and nozzle from our catalog to your job.</p>

            <div className="mt-10 h-1 bg-neutral-200 max-w-3xl"><div className="h-1 bg-brand-primary transition-[width] duration-500" style={{ width: `${progress}%` }} data-testid="finder-progress" /></div>

            <div className="mt-10">
                {step ? (
                    <div>
                        <div className="flex items-center justify-between max-w-3xl mb-6">
                            <h2 className="font-display font-black uppercase text-2xl md:text-4xl text-neutral-900" data-testid="finder-question">{step.title}</h2>
                            <span className="label-caps text-xs text-neutral-500">{stepIdx + 1} / {STEPS.length}</span>
                        </div>
                        <div className="max-w-3xl"><Options step={step} onPick={pick} selected={answers[step.key]} /></div>
                        {stepIdx > 0 && (
                            <button onClick={() => setStepIdx(stepIdx - 1)} className="mt-6 inline-flex items-center gap-2 label-caps text-sm text-neutral-600 hover:text-neutral-900" data-testid="finder-back"><ArrowLeft className="h-4 w-4" /> Back</button>
                        )}
                    </div>
                ) : (
                    <Result answers={answers} catalog={catalog} rules={content?.finder} onRestart={restart} />
                )}
            </div>
        </div>
    );
};

export default SurfaceSolutionFinder;
